import React, { useContext } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { AppContext } from '@/App';
import { User, Database, Users, Shield, ShieldCheck, Settings as SettingsIcon } from 'lucide-react';

function Settings() {
  const { user } = useContext(AppContext);
  const location = useLocation();
  
  // Check if user has admin/full access
  const hasFullAccess = user?.role === 'Admin' || user?.role === 'Manager';

  // Settings tabs
  const tabs = [
    { 
      path: '/settings', 
      label: 'Profile', 
      icon: User, 
      exact: true,
      description: 'Your profile and preferences'
    },
    ...(hasFullAccess ? [
      { 
        path: '/settings/backend', 
        label: 'Backend Settings', 
        icon: Database,
        description: 'Google Sheets sync & data export'
      },
      { 
        path: '/settings/users', 
        label: 'User Management', 
        icon: Users,
        description: 'Manage users and permissions'
      },
      { 
        path: '/settings/permissions-audit', 
        label: 'Permissions Audit', 
        icon: Shield,
        description: 'Preview what any user can see/edit/delete'
      }
    ] : []),
    // Data Integrity — available to Admin OR users granted page_access
    ...((user?.role === 'Admin' || (user?.page_access || {})['/settings/data-integrity']) ? [
      {
        path: '/settings/data-integrity',
        label: 'Data Integrity',
        icon: ShieldCheck,
        description: 'Duplicates, orphans, counter drift scan'
      }
    ] : [])
  ];

  return (
    <div className="space-y-6" data-testid="settings-container">
      {/* Settings Header */}
      <div className="flex items-center gap-3 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="p-2 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg">
          <SettingsIcon className="text-white" size={24} />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500">Manage your account and system settings</p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="flex flex-wrap border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.exact 
              ? location.pathname === tab.path 
              : location.pathname.startsWith(tab.path);
            
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                end={tab.exact}
                className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-all border-b-2 ${
                  isActive
                    ? 'text-blue-600 border-blue-600 bg-blue-50/50'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </NavLink>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-4 sm:p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export default Settings;
